// chunkedUpload.js
// -----------------
// Smart file-upload helper that transparently routes large files through the
// chunked-upload API (bypasses Cloudflare / K8s ingress body-size limits) and
// small files through the plain /api/upload endpoint.
//
// Callers get back the SAME file document shape either way, so components can
// stay agnostic to which path was used.
//
// Usage:
//   const fileDoc = await uploadFileSmart(file, { kind: "document" });
//   const fileDoc = await uploadFileSmart(file, { kind: "image", onProgress: p => ... });

import { API } from "@/lib/api";
import { TOKEN_KEY } from "@/lib/platform";

// Files at-or-below this go through the single-shot POST /api/upload.
// Anything larger switches to chunked upload. 6 MB keeps us well below
// Cloudflare's typical 100 MB inbound cap even after multipart framing overhead.
const SMALL_FILE_THRESHOLD = 6 * 1024 * 1024;

function authHeaders() {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function uploadSmall(file, kind) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("kind", kind);
  const res = await fetch(`${API}/upload`, {
    method: "POST",
    headers: authHeaders(),
    body: fd,
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

async function uploadChunked(file, kind, onProgress) {
  // 1. init
  const initFd = new FormData();
  initFd.append("filename", file.name || "file.bin");
  initFd.append("kind", kind);
  initFd.append("content_type", file.type || "application/octet-stream");
  const initRes = await fetch(`${API}/upload/init`, {
    method: "POST",
    headers: authHeaders(),
    body: initFd,
  });
  if (!initRes.ok) throw new Error(await initRes.text());
  const { upload_id, chunk_size } = await initRes.json();

  // 2. stream each slice in order.
  const total = Math.max(1, Math.ceil(file.size / chunk_size));
  for (let i = 0; i < total; i++) {
    const start = i * chunk_size;
    const end = Math.min(file.size, start + chunk_size);
    const slice = file.slice(start, end);

    const fd = new FormData();
    fd.append("index", String(i));
    fd.append("chunk", slice, `${file.name}.part${i}`);
    const r = await fetch(`${API}/upload/${upload_id}/chunk`, {
      method: "POST",
      headers: authHeaders(),
      body: fd,
    });
    if (!r.ok) throw new Error(await r.text());

    if (onProgress) {
      try {
        onProgress({ index: i + 1, total, bytes: end, size: file.size });
      } catch {
        /* progress-cb errors should never break the upload */
      }
    }
  }

  // 3. finalize.
  const finFd = new FormData();
  finFd.append("total_chunks", String(total));
  const finRes = await fetch(`${API}/upload/${upload_id}/finalize`, {
    method: "POST",
    headers: authHeaders(),
    body: finFd,
  });
  if (!finRes.ok) throw new Error(await finRes.text());
  return finRes.json();
}

/**
 * Upload a File and get back a file document identical in shape to the one
 * returned by POST /api/upload. Automatically picks the right transport based
 * on file size.
 *
 * @param {File} file
 * @param {{kind?: "document"|"image", onProgress?: (p:{index,total,bytes,size})=>void}} opts
 */
export async function uploadFileSmart(file, opts = {}) {
  const kind = opts.kind || (file.type?.startsWith("image/") ? "image" : "document");
  if (file.size <= SMALL_FILE_THRESHOLD) {
    return uploadSmall(file, kind);
  }
  return uploadChunked(file, kind, opts.onProgress);
}

export default uploadFileSmart;
